import React, { useState, useRef, useEffect } from 'react';
import { FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import './CategoryList.scss';

interface Category {
  id: string;
  name: string;
  sentiment?: string | null;
  parentId: string;
}

interface CategoryListProps {
  categories: Category[];
  onSelect: (categoryId: string | null) => void;
  selectedCategoryId?: string | null;
}

function CategoryList({ categories, onSelect, selectedCategoryId }: CategoryListProps) {
  const [selectedId, setSelectedId] = useState<string | null>(selectedCategoryId || null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Update selected category when prop changes
  useEffect(() => {
    setSelectedId(selectedCategoryId || null);
  }, [selectedCategoryId]);

  // Check scroll capabilities
  const checkScrollCapabilities = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  // Initial scroll check and resize listener
  useEffect(() => {
    const checkScroll = () => {
      checkScrollCapabilities();
    };

    checkScroll();
    window.addEventListener('resize', checkScroll);

    return () => {
      window.removeEventListener('resize', checkScroll);
    };
  }, [categories]);

  // Handle category selection
  const handleCategorySelect = (categoryId: string) => {
    // Toggle behavior: if already selected, clear the filter
    if (selectedId === categoryId) {
      setSelectedId(null);
      onSelect(null);
      return;
    }
    setSelectedId(categoryId);
    onSelect(categoryId);
  };

  // Scroll functions
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      const scrollAmount = scrollContainerRef.current.clientWidth * 0.8;
      scrollContainerRef.current.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      const scrollAmount = scrollContainerRef.current.clientWidth * 0.8;
      scrollContainerRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  // Get sentiment class for styling
  const getSentimentClass = (sentiment?: string | null) => {
    const normalized = typeof sentiment === 'string' ? sentiment.trim().toLowerCase() : '';

    switch (normalized) {
      case 'positive':
        return 'sentiment-positive';
      case 'negative':
        return 'sentiment-negative';
      case 'neutral':
        return 'sentiment-neutral';
      default:
        return 'sentiment-default';
    }
  };

  return (
    <div className="category-list_container">
      {canScrollLeft && (
        <button
          className="category-list_scroll-button scroll-button-left"
          onClick={scrollLeft}
          aria-label="Scroll left"
        >
          <FaChevronLeft />
        </button>
      )}

      <div
        ref={scrollContainerRef}
        className="category-list_scroll-section"
        onScroll={checkScrollCapabilities}
      >
        <div className="category-list_pill-container">
          {categories.map((category) => (
            <button
              key={category.id}
              className={`category-list_pill ${getSentimentClass(category.sentiment)} ${
                selectedId === category.id ? 'cl_selected' : ''
              }`}
              onClick={() => handleCategorySelect(category.id)}
              title={`${category.name} (${category.sentiment})`}
            >
              <span className="category-list_pill-name">{category.name}</span>
              {selectedId === category.id && (
                <span
                  className="category-list_pill-close"
                  aria-label="Clear filter"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedId(null);
                    onSelect(null);
                  }}
                >
                  <FaTimes />
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {canScrollRight && (
        <button
          className="category-list_scroll-button scroll-button-right"
          onClick={scrollRight}
          aria-label="Scroll right"
        >
          <FaChevronRight />
        </button>
      )}
    </div>
  );
}

export default CategoryList;
